"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const events_1 = require("events");
const connection_1 = require("./transmission/connection");
const channel_1 = require("./transmission/channel");
const resolve_auth_1 = require("./resolve-auth");
const events_2 = require("./const/events");
const createQueryString = (query) => {
    if (query !== null && typeof query && Object.keys(query).length > 0) {
        return ("?" +
            Object.keys(query)
                .map((key) => `${key}=${encodeURIComponent(query[key])}`)
                .join("&"));
    }
    return "";
};
const createConnection = (options) => {
    const scheme = options.useTls ? "wss" : "ws";
    const host = options.wsHost;
    const port = options.useTls ? options.wssPort : options.wsPort;
    const path = options.appId || "LiquidTapClient";
    return new connection_1.Connection(`${scheme}://${host}:${port}/app/${path}${createQueryString(options.query)}`, options.connectionConfig);
};
const defaultOptions = {
    wsHost: "tap.liquid.com",
    wsPort: 443,
    wssPort: 443,
    useTls: true,
    requireValidSsl: true,
    auth: null,
    query: null,
    appId: "LiquidTapClient",
    authenticationTimeout: 5000,
    authRetryDelay: 200,
    authRetryMaxAttempts: 3,
};
class TapClient {
    constructor(options, connection) {
        this.options = Object.assign(defaultOptions, options);
        this.channels = [];
        /* WebSocket Initialisation */
        if (connection !== undefined) {
            this.connection = connection;
        }
        else {
            this.connection = createConnection(this.options);
            this.connection.open();
        }
        this._bindConnection();
        this.authenticated = false;
        this.authenticating = false;
        this.authenticationPending =
            this.options.auth !== null && this.options.auth !== undefined;
        this.authRetryAttempts = 0;
        this.authRetryTimer = null;
        this.tagReconnection = false;
        this.pendingChannels = {
            waitingForConnection: [],
            waitingForAuth: [],
        };
        this.eventEmitter = new events_1.EventEmitter();
        this.authenticationTimer = null;
        this.identity = Date.now().toString();
    }
    /* ----------- PUBLIC ----------- */
    connected() {
        return this.connection.connected();
    }
    subscribe(channelName, payload) {
        const channel = this.getChannel(channelName, true, payload);
        if (channel.subscriptionPending) {
            return channel;
        }
        if (channel.subscriptionCancelled) {
            channel.reinstateSubscription();
            return channel;
        }
        if (this.authenticating || this.authenticationPending) {
            this.pendingChannels.waitingForAuth.push(channel);
            return channel;
        }
        if (!this.connected()) {
            this.pendingChannels.waitingForConnection.push(channel);
            return channel;
        }
        channel.subscribe();
        return channel;
    }
    sendEvent(eventName, data, channelName) {
        if (channelName !== undefined) {
            const channel = this.getChannel(channelName);
            if (channel !== null) {
                channel.stats.sent += 1;
            }
        }
        return this.connection.sendEvent(eventName, data, channelName);
    }
    bind(event, handler) {
        if (handler !== undefined) {
            this.eventEmitter.on(event, handler);
            return;
        }
        return new Promise((resolve) => this.eventEmitter.on(event, resolve));
    }
    authenticate(auth) {
        this.options.auth = auth;
        this.authenticationPending = true;
        const self = this;
        return new Promise((resolve, reject) => {
            self.bind(events_2.CLIENT_EVENTS.AUTHENTICATED, resolve);
            self.bind(events_2.CLIENT_EVENTS.AUTHENTICATION_ABORTED, reject);
            this._maybeAuthenticateWithRetry();
        });
    }
    unsubscribe(channelName) {
        const channel = this.getChannel(channelName, false);
        if (channel !== null) {
            channel.unsubscribe();
            return true;
        }
        return false;
    }
    removeChannel(name) {
        this.channels = this.channels.filter((c) => c.name !== name);
    }
    getChannel(name, createIfNotExists, subPayload) {
        const channel = this.channels.find((ch) => ch.name === name);
        if (channel !== undefined) {
            return channel;
        }
        if (createIfNotExists === true) {
            const newChannel = new channel_1.Channel(name, this, subPayload);
            this.channels.push(newChannel);
            return newChannel;
        }
        return null;
    }
    destroy() {
        for (const channel of this.channels) {
            channel.unsubscribe();
        }
        this.connection.close();
    }
    /* ----------- PRIVATE ----------- */
    _sendAuthentication(jwt) {
        // Now there is a defined user, so use this for future hash entropy
        this.identity = jwt.substring(10, 24);
        const authPayload = {
            path: "/realtime",
            headers: {
                "X-Quoine-Auth": jwt,
            },
        };
        const self = this;
        this.authenticationTimer = setTimeout(this._authenticationTimeout.bind(this), this.options.authenticationTimeout);
        if (self.connected()) {
            // Auth now
            self.sendEvent(events_2.SERVER_EVENTS.UP.AUTH_REQUEST, authPayload);
        }
        else {
            // Auth later
            self.eventEmitter.on(events_2.CONNECTION_STATE.CONNECTED, () => {
                // Authing
                self.sendEvent(events_2.SERVER_EVENTS.UP.AUTH_REQUEST, authPayload);
            });
        }
        return Promise.resolve();
    }
    _authenticationTimeout() {
        this._handleAuthFailure(`Authentication timed out: ${this.options.authenticationTimeout}ms`);
    }
    _handleTick(dataObj) {
        const channelName = dataObj.channel;
        const eventName = dataObj.event;
        const { data } = dataObj;
        if (channelName !== undefined) {
            for (const channel of this.channels) {
                if (channel.name === channelName) {
                    channel.notify(dataObj);
                }
            }
        }
        this.eventEmitter.emit(eventName, data);
    }
    _handleConnected({ reconnect }) {
        this._subscribeAllWaiting("waitingForConnection");
        this._maybeAuthenticateWithRetry();
        if (reconnect) {
            this.tagReconnection = true;
            for (const channel of this.channels) {
                channel.subscribe();
            }
        }
        this.eventEmitter.emit(events_2.CLIENT_EVENTS.CONNECTED, { reconnect });
    }
    _handleDisconnected() {
        this.eventEmitter.emit(events_2.CLIENT_EVENTS.DISCONNECTED, null);
    }
    _handleUnavailable() {
        this.eventEmitter.emit(events_2.CLIENT_EVENTS.UNAVAILABLE, null);
    }
    _handleAuthSuccess() {
        this.authenticated = true;
        this.authenticating = false;
        this.authenticationPending = false;
        if (this.authenticationTimer !== null) {
            clearTimeout(this.authenticationTimer);
            this.authenticationTimer = null;
        }
        this._subscribeAllWaiting("waitingForAuth");
        this.eventEmitter.emit(events_2.CLIENT_EVENTS.AUTHENTICATED, {
            reconnect: this.tagReconnection,
        });
    }
    _handleAuthFailure(data) {
        this.authenticated = false;
        this.authenticating = false;
        this.authenticationPending = false;
        if (this.authenticationTimer !== null) {
            clearTimeout(this.authenticationTimer);
            this.authenticationTimer = null;
        }
        this._maybeScheduleAuthRetry();
        this.eventEmitter.emit(events_2.CLIENT_EVENTS.AUTHENTICATION_FAILED, {
            message: data,
            attempts: this.authRetryAttempts,
        });
    }
    _canSubscribeNow(channel) {
        const connectionReady = this.connected();
        const authReady = !this.authenticating || this.authenticated;
        const channelReady = !channel.subscriptionPending;
        return connectionReady && authReady && channelReady;
    }
    _subscribeAllWaiting(queue) {
        for (const channel of this.pendingChannels[queue]) {
            channel.subscribe();
        }
        this.pendingChannels[queue] = [];
    }
    /**
     * Checks if the required information is present, then attempts authentication and schedules retries if it fails.
     *
     * Returns a promise which resolves when the auth either succeeds or fails.
     */
    _maybeAuthenticateWithRetry() {
        if (this.options.auth !== null &&
            this.options.auth !== undefined &&
            !this.authenticating) {
            this.authenticating = true;
            this.authRetryAttempts = 0;
            return this._attemptAuthOnce();
        }
        return Promise.resolve();
    }
    /**
     * Performs a single authentication attempt.
     */
    _attemptAuthOnce() {
        if (this.options.auth !== null && this.options.auth !== undefined) {
            this.authRetryAttempts += 1;
            const resolver = resolve_auth_1.resolveAuth(this.options.auth, "/realtime");
            return resolver(this._sendAuthentication.bind(this));
        }
        return Promise.resolve();
    }
    /**
     * Schedules another authention attempt if the retry limit isn't exceeded
     */
    _maybeScheduleAuthRetry() {
        if (this.authRetryAttempts < this.options.authRetryMaxAttempts) {
            const self = this;
            this.authRetryTimer = setTimeout(() => {
                self._attemptAuthOnce().catch(() => { }); // surpress rejection here
            }, this.options.authRetryDelay);
        }
        else {
            this.eventEmitter.emit(events_2.CLIENT_EVENTS.AUTHENTICATION_ABORTED, {
                attempts: this.authRetryAttempts,
            });
        }
    }
    _bindConnection() {
        this.connection.bind(events_2.CLIENT_EVENTS.TICK_RECEIVED, this._handleTick.bind(this));
        this.connection.bind(events_2.CONNECTION_STATE.CONNECTED, this._handleConnected.bind(this));
        this.connection.bind(events_2.CONNECTION_STATE.DISCONNECTED, this._handleDisconnected.bind(this));
        this.connection.bind(events_2.CONNECTION_STATE.UNAVAILABLE, this._handleUnavailable.bind(this));
        this.connection.bind(events_2.SERVER_EVENTS.DOWN.AUTH_SUCCESS, this._handleAuthSuccess.bind(this));
        this.connection.bind(events_2.SERVER_EVENTS.DOWN.AUTH_FAILURE, this._handleAuthFailure.bind(this));
    }
}
exports.TapClient = TapClient;
